/**
 * MessageBubble
 *
 * Individual message bubble with sender info, timestamp, reply preview,
 * edit inline, and click-to-toggle inline action menus.
 *
 * Click a message → emoji row above + action row below appear inline.
 * Click again or click another message → menus close.
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar } from '@/components/ui';
import { MESSAGE_TYPES } from '@/models/Conversation';
import {
  CheckIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { ReactionDisplay } from './ReactionPicker';
import MessageStatusTicks from './MessageStatusTicks';
import { getMediaRenderer } from './messageRenderers';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const MORE_REACTIONS = ['✅', '🎉', '🦢', '👏', '🔥', '💯'];

const inlineMenuVariants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto', transition: { duration: 0.15, ease: 'easeOut' } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.1, ease: 'easeIn' } }
};

export default function MessageBubble({
  message,
  isOwn,
  isGrouped,
  isGroup,
  isEditing,
  editText,
  setEditText,
  onSaveEdit,
  onCancelEdit,
  onReply,
  onForward,
  onEdit,
  onDeleteForMe,
  onDeleteForEveryone,
  currentUserId,
  onAddReaction,
  onRemoveReaction,
  activeMessageId,
  setActiveMessageId
}) {
  const [showMoreEmoji, setShowMoreEmoji] = useState(false);
  const isActive = activeMessageId === message.id;
  const isSystem = message.type === MESSAGE_TYPES.SYSTEM;
  const isDeleted = message.deletedForEveryone;

  const handleBubbleClick = () => {
    if (isEditing) return;
    if (isActive) setShowMoreEmoji(false);
    setActiveMessageId?.(isActive ? null : message.id);
  };

  const handleAction = (fn) => {
    setActiveMessageId?.(null);
    fn?.();
  };

  // System messages
  if (isSystem) {
    return (
      <div className="flex items-center justify-center py-1">
        <span className="px-3 py-1 bg-base-200 rounded-full text-xs text-base-content/60">
          {message.text}
        </span>
      </div>
    );
  }

  // Deleted message
  if (isDeleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${!isGrouped ? 'mt-3' : ''}`}>
        <div className="px-4 py-2 rounded-2xl bg-base-200 italic text-base-content/40 text-sm">
          This message was deleted
        </div>
      </div>
    );
  }

  const messageTime = message.sentAt?.toDate?.()
    ? format(message.sentAt.toDate(), 'HH:mm')
    : '';

  return (
    <motion.div
      id={`msg-${message.id}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'} ${!isGrouped ? 'mt-3' : ''} group transition-colors duration-500`}
    >
      {/* Avatar (only for others, not grouped) */}
      {!isOwn && !isGrouped ? (
        <div className="flex-shrink-0 self-end">
          <Avatar name={message.senderName} size="xs" />
        </div>
      ) : !isOwn ? (
        <div className="w-6" />
      ) : null}

      {/* Bubble + inline menus */}
      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[75%]`}>

        {/* Emoji row — above bubble (not on own messages) */}
        <AnimatePresence>
          {isActive && !isEditing && !isOwn && (
            <motion.div
              key="emoji-row"
              variants={inlineMenuVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="overflow-hidden"
            >
              <div className="flex items-center gap-1 px-1 py-1 mb-1 flex-wrap">
                {QUICK_REACTIONS.map(emoji => {
                  const reacted = message.reactions?.[emoji]?.includes(currentUserId);
                  return (
                    <button
                      key={emoji}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-lg hover:scale-125 active:scale-95 transition-all duration-150 cursor-pointer ${
                        reacted ? 'bg-primary/20 ring-1 ring-primary/40' : 'hover:bg-base-200'
                      }`}
                      onClick={(e) => { e.stopPropagation(); onAddReaction?.(message.id, emoji); }}
                    >
                      {emoji}
                    </button>
                  );
                })}
                {!showMoreEmoji && (
                  <button
                    className="w-8 h-8 flex items-center justify-center rounded-full text-base-content/40 hover:bg-base-200 hover:text-base-content/70 transition-all duration-150 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setShowMoreEmoji(true); }}
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                )}
                {showMoreEmoji && MORE_REACTIONS.map(emoji => {
                  const reacted = message.reactions?.[emoji]?.includes(currentUserId);
                  return (
                    <button
                      key={emoji}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-lg hover:scale-125 active:scale-95 transition-all duration-150 cursor-pointer ${
                        reacted ? 'bg-primary/20 ring-1 ring-primary/40' : 'hover:bg-base-200'
                      }`}
                      onClick={(e) => { e.stopPropagation(); onAddReaction?.(message.id, emoji); }}
                    >
                      {emoji}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* The bubble */}
        <div className="relative">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                className="input input-bordered input-sm text-sm"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveEdit();
                  if (e.key === 'Escape') onCancelEdit();
                }}
                autoFocus
              />
              <button className="btn btn-ghost btn-xs btn-circle" onClick={onSaveEdit}>
                <CheckIcon className="w-3 h-3 text-success" />
              </button>
              <button className="btn btn-ghost btn-xs btn-circle" onClick={onCancelEdit}>
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div
              className={`px-3 py-1.5 rounded-xl text-sm cursor-pointer ${
                isOwn
                  ? 'bg-primary text-primary-content rounded-br-sm'
                  : 'bg-base-200 text-base-content rounded-bl-sm'
              } ${isActive ? 'ring-2 ring-primary/30' : ''}`}
              onClick={handleBubbleClick}
            >
              {/* Sender name (only in group chats, not grouped) */}
              {isGroup && !isOwn && !isGrouped && (
                <span className="text-xs font-medium block mb-0.5 text-base-content/60">
                  {message.senderName}
                </span>
              )}

              {/* Forwarded label */}
              {message.forwardedFrom && (
                <div className={`text-xs italic mb-1 ${isOwn ? 'text-primary-content/60' : 'text-base-content/50'}`}>
                  Forwarded from {message.forwardedFrom.senderName}
                </div>
              )}

              {/* Reply preview */}
              {message.replyTo && (
                <div className={`text-xs mb-1 pb-1 border-b ${
                  isOwn ? 'border-primary-content/20' : 'border-base-content/10'
                }`}>
                  <span className="font-semibold">{message.replyTo.senderName}</span>
                  <p className="truncate opacity-80">{message.replyTo.text}</p>
                </div>
              )}

              {/* Media content — dispatched via renderer map */}
              {message.media && (() => {
                const MediaRenderer = getMediaRenderer(message.type);
                return MediaRenderer
                  ? <MediaRenderer media={message.media} isOwn={isOwn} />
                  : null;
              })()}

              {/* Text + inline timestamp */}
              <div className="relative whitespace-pre-wrap break-words">
                {message.text}
                {/* Invisible spacer to reserve room for timestamp */}
                {!isGrouped && (
                  <span className="invisible text-[10px] ml-2 inline-flex items-center gap-1">
                    {messageTime}
                    {message.editedAt && <span>edited</span>}
                    {isOwn && <span className="w-3 h-3" />}
                  </span>
                )}
                {/* Actual timestamp, positioned at bottom-right */}
                {!isGrouped && (
                  <span className={`absolute bottom-0 right-0 text-[10px] inline-flex items-center gap-1 ${
                    isOwn ? 'text-primary-content/50' : 'text-base-content/40'
                  }`}>
                    {messageTime}
                    {message.editedAt && <span className="italic">edited</span>}
                    {isOwn && <MessageStatusTicks status={message.status} isOwn />}
                  </span>
                )}
              </div>

              {/* Reactions — inside bubble, Telegram style */}
              <ReactionDisplay
                reactions={message.reactions}
                currentUserId={currentUserId}
                onAdd={(emoji) => onAddReaction?.(message.id, emoji)}
                onRemove={(emoji) => onRemoveReaction?.(message.id, emoji)}
              />
            </div>
          )}
        </div>

        {/* Action row — below bubble */}
        <AnimatePresence>
          {isActive && !isEditing && (
            <motion.div
              key="action-row"
              variants={inlineMenuVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="overflow-hidden"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-1 mt-1">
                <InlineAction icon={ArrowUturnLeftIcon} label="Reply" onClick={() => handleAction(onReply)} />
                <InlineAction icon={ArrowUturnRightIcon} label="Forward" onClick={() => handleAction(onForward)} />
                {isOwn && (
                  <InlineAction icon={PencilIcon} label="Edit" onClick={() => handleAction(onEdit)} />
                )}
                <InlineAction
                  icon={TrashIcon}
                  label={isOwn ? 'Delete for everyone' : 'Delete for me'}
                  onClick={() => {
                    const label = isOwn ? 'delete this message for everyone' : 'delete this message for you';
                    if (window.confirm(`Are you sure you want to ${label}? This cannot be undone.`)) {
                      handleAction(isOwn ? onDeleteForEveryone : onDeleteForMe);
                    }
                  }}
                  danger
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </motion.div>
  );
}

function InlineAction({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      className={`flex items-center gap-1 text-xs py-0.5 rounded hover:bg-base-200 active:bg-base-300 transition-colors cursor-pointer ${
        danger ? 'text-error' : 'text-base-content/70'
      }`}
      onClick={onClick}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
